import React, { Component } from 'react';
import SubNav from '../sub_nav/sub_nav';

const SLIDE_TYPE_OPTIONS = [
    { value: '', label: 'Not Used' },
    { value: 'point', label: 'Body / Sermon' },
    { value: 'scripture', label: 'Scripture / Reference' },
    { value: 'song', label: 'Song Lyrics' },
];

class ThemeUploadPicker extends Component {
    constructor(props) {
        super(props);
        const { parsedTheme } = props;
        this.state = {
            name: parsedTheme.name || 'My Theme',
            mappings: (parsedTheme.slides || []).map(slide => ({
                ...slide,
                assignedType: slide.suggested_type || '',
            })),
            activate: true,
        };
    }

    handleMappingChange = (index, newType) => {
        const mappings = [...this.state.mappings];
        mappings[index] = { ...mappings[index], assignedType: newType };
        this.setState({ mappings });
    }

    handleSave = () => {
        const slides = this.state.mappings
            .filter(m => m.assignedType)
            .map(m => ({
                type: m.assignedType,
                font_name: m.font_name,
                font_family: m.font_family,
                font_size: m.font_size,
                text_color: m.text_color,
                alignment: m.alignment,
                vertical_alignment: m.vertical_alignment,
                position: m.position,
                size: m.size,
                background_color: m.background_color,
            }));

        this.props.onSave({
            name: this.state.name,
            source_filename: this.props.parsedTheme.source_filename,
            activate: this.state.activate,
            slides: slides,
        });
    }

    rgbaString(color) {
        if (!color) return '#ffffff';
        const r = Math.round((color.r || 0) * 255);
        const g = Math.round((color.g || 0) * 255);
        const b = Math.round((color.b || 0) * 255);
        return 'rgb(' + r + ',' + g + ',' + b + ')';
    }

    render() {
        const { mappings, name, activate } = this.state;

        return (
            <div className="max-w-4xl mx-auto p-6">
                <h1 className="text-2xl font-bold text-gray-800 mb-2">Upload Theme</h1>
                <p className="text-gray-500 mb-6">
                    Assign each slide template from your .protheme file to a WorshipKit slide type.
                </p>

                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Theme Name</label>
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => this.setState({ name: e.target.value })}
                        className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-400"
                    />
                </div>

                <div className="space-y-4 mb-6">
                    {mappings.map((slide, index) => (
                        <div key={index} className="flex items-stretch border border-gray-200 rounded-lg overflow-hidden">
                            {/* Preview card — scaled from 1920x1080 canvas */}
                            {(() => {
                                const PREVIEW_W = 240;
                                const scale = PREVIEW_W / 1920;
                                const PREVIEW_H = PREVIEW_W * (1080 / 1920);
                                return (
                                    <div
                                        className="shrink-0 bg-gray-800"
                                        style={{ width: PREVIEW_W, height: PREVIEW_H, position: 'relative', overflow: 'hidden' }}
                                    >
                                        <div style={{
                                            position: 'absolute',
                                            left: (slide.position?.x || 0) * scale,
                                            top: (slide.position?.y || 0) * scale,
                                            width: (slide.size?.width || 1920) * scale,
                                            height: (slide.size?.height || 1080) * scale,
                                            fontFamily: (slide.font_family || 'sans-serif') + ', sans-serif',
                                            fontSize: Math.max((slide.font_size || 48) * scale, 6) + 'px',
                                            color: this.rgbaString(slide.text_color),
                                            textAlign: slide.alignment || 'center',
                                            display: 'flex',
                                            alignItems: slide.vertical_alignment === 'bottom' ? 'flex-end' : slide.vertical_alignment === 'top' ? 'flex-start' : 'center',
                                            justifyContent: slide.alignment === 'left' ? 'flex-start' : slide.alignment === 'right' ? 'flex-end' : 'center',
                                            lineHeight: 1.2,
                                            overflow: 'hidden',
                                        }}>
                                            Sample Text
                                        </div>
                                        <div className="absolute bottom-0 left-0 right-0 px-2 py-1 bg-gray-900/75">
                                            <p className="text-xs text-gray-400">
                                                "{slide.name}" &mdash; {slide.font_name || 'Unknown'} {slide.font_size ? (Math.round(slide.font_size) + 'pt') : ''}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })()}

                            {/* Mapping dropdown */}
                            <div className="flex-1 flex items-center px-6 bg-white">
                                <div className="w-full">
                                    <label className="block text-xs text-gray-400 mb-1">Use as:</label>
                                    <select
                                        value={slide.assignedType}
                                        onChange={(e) => this.handleMappingChange(index, e.target.value)}
                                        className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-400"
                                    >
                                        {SLIDE_TYPE_OPTIONS.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="flex items-center mb-6">
                    <input
                        type="checkbox"
                        id="activate-theme"
                        checked={activate}
                        onChange={(e) => this.setState({ activate: e.target.checked })}
                        className="mr-2"
                    />
                    <label htmlFor="activate-theme" className="text-sm text-gray-600">Set as active theme</label>
                </div>

                <div className="flex space-x-3">
                    <button
                        onClick={this.handleSave}
                        disabled={!name.trim()}
                        className="bg-blue-500 text-white px-6 py-2 rounded-sm hover:bg-blue-600 text-sm font-medium disabled:opacity-50"
                    >
                        Save Theme
                    </button>
                    <button
                        onClick={this.props.onCancel}
                        className="text-gray-500 px-6 py-2 rounded-sm border border-gray-300 hover:bg-gray-50 text-sm"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        );
    }
}

export default ThemeUploadPicker;
